import clsx from 'clsx'
import PropTypes from 'prop-types'
import DatePicker, { registerLocale } from 'react-datepicker'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import de from 'date-fns/locale/de'

import { TLanguages } from '../../shared/const'

import styles from './style.module.scss'
import {
	endOfMonth,
	format as formatDate,
	getMonth,
	getYear,
	isAfter,
	isBefore,
	isValid,
	parse,
	setMonth,
	setYear,
	startOfMonth
} from 'date-fns'
import { fromWallClock, toWallClock } from '../../shared/datetime'

/*
 * Datum und Uhrzeit im oesterreichischen Format.
 *
 * react-datepicker liefert die Uhrzeitliste sonst zwoelfstuendig aus ("2:30 PM")
 * und die Monats- und Wochentagsnamen englisch. Beides passt nicht zu einer
 * Anwendung, die ausschliesslich in Oesterreich benutzt wird: Hier ist 13:00 die
 * gewohnte Schreibweise, und AM/PM zwingt zum Umrechnen.
 */
registerLocale(TLanguages.DE, de)

/*
 * Das Feld rechnet in Wiener Zeit, nicht in der Zeitzone des Rechners.
 *
 * react-datepicker kennt nur die Browser-Zone: Es zeigt die lokalen Felder eines
 * Date an und gibt beim Auswaehlen ein Date mit lokalen Feldern zurueck. Nach
 * aussen — zum Formular und damit zur Schnittstelle — bleibt der Wert ein echter
 * Zeitpunkt; nur innerhalb des Pickers wird mit Wiener Wanduhrzeit gearbeitet
 * (toWallClock hinein, fromWallClock heraus). Sonst legt eine Nutzer:in mit
 * abweichend eingestelltem Rechner eine andere Uhrzeit ab, als sie sieht.
 *
 * minDate/maxDate muessen mitverschoben werden, sonst vergleicht der Kalender
 * Wanduhrzeit gegen Zeitpunkt und sperrt Tage an den Raendern falsch — das gilt
 * auch fuer die Monats- und Jahresauswahl im Kopf des Kalenders.
 */
const TIME_FORMAT = 'HH:mm'

/*
 * Spanne der Jahresauswahl, wenn das Feld selbst keine Grenzen mitbringt: weit
 * genug zurueck fuer Geburtsdaten, weit genug voraus fuer Termine.
 */
const JAHRE_ZURUECK = 100
const JAHRE_VORAUS = 10

const MONATE = Array.from({ length: 12 }, (_, index) => new Date(2000, index, 1))

function jahresListe(von, bis) {
	return Array.from({ length: bis - von + 1 }, (_, index) => von + index)
}

export default function DateTimeField(props) {
	const { t, i18n } = useTranslation()

	const minDate = toWallClock(props.minDate)
	const maxDate = toWallClock(props.maxDate)

	const locale = i18n.language === TLanguages.EN ? undefined : TLanguages.DE
	const monatsName = date => formatDate(date, 'LLLL', { locale: locale ? de : undefined })

	/*
	 * Monat und Jahr als gewoehnliche Auswahlfelder.
	 *
	 * Die eingebauten Listen von react-datepicker zeichnet die Anwendung selbst
	 * in den Kalender hinein. Sie klappen immer nach unten auf, und weil der
	 * Kalender ohnehin schon unter dem Eingabefeld haengt, endet die Liste
	 * regelmaessig unterhalb des Bildschirmrands: Der untere Teil ist nicht zu
	 * sehen und nicht zu scrollen — im Fenster "Nachricht duplizieren" war so
	 * etwa das naechste Jahr nicht auswaehlbar. Ein gewoehnliches Auswahlfeld
	 * zeichnet der Browser selbst, klappt zur Seite auf, an der Platz ist, und
	 * bringt seine eigene Bildlaufleiste mit. Ausserdem laesst sich die Jahres-
	 * zahl darin einfach tippen.
	 */
	function renderHeader(
		{
			date,
			changeYear,
			changeMonth,
			decreaseMonth,
			increaseMonth,
			prevMonthButtonDisabled,
			nextMonthButtonDisabled
		},
		field
	) {
		const heute = toWallClock(new Date())
		const erstesJahr = minDate ? getYear(minDate) : getYear(heute) - JAHRE_ZURUECK
		const letztesJahr = maxDate ? getYear(maxDate) : getYear(heute) + JAHRE_VORAUS
		// Das eingestellte Jahr gehoert in die Liste, auch wenn es ausserhalb der
		// Spanne liegt — sonst zeigt das Feld einen leeren Wert.
		const jahre = jahresListe(Math.min(erstesJahr, getYear(date)), Math.max(letztesJahr, getYear(date), erstesJahr))

		const monatGesperrt = (monat, jahr = getYear(date)) => {
			const ersterTag = startOfMonth(new Date(jahr, monat, 1))
			const letzterTag = endOfMonth(ersterTag)

			return (
				(minDate && isBefore(letzterTag, minDate)) || (maxDate && isAfter(ersterTag, maxDate))
			)
		}

		/*
		 * Was im Kopf gewaehlt wird, geht auch in den Wert.
		 *
		 * react-datepicker blaettert damit nur den Kalender weiter: Wer das Jahr
		 * umstellt, sieht den neuen Jahrgang, im Feld steht aber weiter das alte
		 * Datum — uebernommen wuerde es erst mit einem Klick auf einen Tag. Genau
		 * den macht niemand, wenn der richtige Tag schon eingetragen ist: Man
		 * stellt das Jahr um, schliesst den Kalender und hat die alte Jahreszahl.
		 *
		 * Nur, wenn das Feld schon ein Datum hat. In einem leeren Feld waere ein
		 * Wert, den niemand eingegeben hat, eine Behauptung — dort entsteht er
		 * weiter mit dem Klick auf den Tag.
		 */
		const wert = field.value ? toWallClock(field.value) : null

		const uebernimm = neuerWert => {
			if (!neuerWert || !isValid(neuerWert)) {
				return
			}

			if ((minDate && isBefore(neuerWert, minDate)) || (maxDate && isAfter(neuerWert, maxDate))) {
				return
			}

			field.onChange(fromWallClock(neuerWert))
		}

		const monatWechseln = monat => {
			changeMonth(monat)

			if (wert) {
				uebernimm(setMonth(wert, monat))
			}
		}

		/*
		 * Beim Jahreswechsel den Monat mitnehmen, wenn er im neuen Jahr ausserhalb
		 * der erlaubten Spanne liegt: Sonst steht der Kalender etwa im Mai eines
		 * Jahres, dessen erste Monate schon vorbei sind, und zeigt lauter graue
		 * Tage.
		 */
		const jahrWechseln = jahr => {
			changeYear(jahr)

			if (!monatGesperrt(getMonth(date), jahr)) {
				if (wert) {
					uebernimm(setYear(wert, jahr))
				}

				return
			}

			const erlaubte = MONATE.map((_, monat) => monat).filter(monat => !monatGesperrt(monat, jahr))

			if (!erlaubte.length) {
				return
			}

			const monat = getMonth(date) < erlaubte[0] ? erlaubte[0] : erlaubte[erlaubte.length - 1]

			changeMonth(monat)

			if (wert) {
				uebernimm(setMonth(setYear(wert, jahr), monat))
			}
		}

		return (
			<div className={styles.calendarHeader}>
				<button
					type="button"
					className={styles.calendarNav}
					onClick={decreaseMonth}
					disabled={prevMonthButtonDisabled}
					aria-label={t('Previous month')}
				>
					‹
				</button>
				<select
					className={styles.calendarSelect}
					value={getMonth(date)}
					onChange={event => monatWechseln(Number(event.target.value))}
					aria-label={t('Month')}
				>
					{MONATE.map((monat, index) => (
						<option key={index} value={index} disabled={monatGesperrt(index)}>
							{monatsName(monat)}
						</option>
					))}
				</select>
				<select
					className={styles.calendarSelect}
					value={getYear(date)}
					onChange={event => jahrWechseln(Number(event.target.value))}
					aria-label={t('Year')}
				>
					{jahre.map(jahr => (
						<option key={jahr} value={jahr}>
							{jahr}
						</option>
					))}
				</select>
				<button
					type="button"
					className={styles.calendarNav}
					onClick={increaseMonth}
					disabled={nextMonthButtonDisabled}
					aria-label={t('Next month')}
				>
					›
				</button>
			</div>
		)
	}

	return (
		<label style={props.mb ? { marginBottom: props.mb } : {}} className={clsx(styles.root, props.className)}>
			{props.error ? (
				<span className={styles.error}>{props.error.message}</span>
			) : (
				<span className={styles.inputName}>
					{props.label} {props.required ? <span className={styles.require}>*</span> : ''}
				</span>
			)}
			<Controller
				name={props.name}
				control={props.control}
				render={({ field }) => (
					<DatePicker
						{...field}
						renderCustomHeader={kopf => renderHeader(kopf, field)}
						onChangeRaw={e => {
							const value = e.target.value
							const parsed = parse(value, props.dateFormat || 'dd.MM.yyyy', new Date())

							if (isValid(parsed)) {
								if ((minDate && isBefore(parsed, minDate)) || (maxDate && isAfter(parsed, maxDate))) {
									return
								}

								field.onChange(fromWallClock(parsed))
							}
						}}
						popperClassName={styles.popperStyles}
						/*
						 * Fest statt absolut positioniert: In einem Dialog, dessen Rumpf
						 * scrollt, schneidet der Rahmen sonst den Kalender ab — man sieht
						 * die halbe Uhrzeitliste und kommt an den Rest nicht heran.
						 * Fest positioniert liegt er ueber allem, auch ueber der
						 * Fussleiste.
						 */
						popperProps={{ strategy: 'fixed' }}
						wrapperClassName={styles.datePicker}
						disabled={props.disabled}
						autoComplete="off"
						selected={toWallClock(field.value)}
						/*
						 * Der Kalender geht an dem Datum auf, das im Feld steht.
						 * react-datepicker fuehrt seine Vorauswahl sonst allein und
						 * blieb nach einer getippten Aenderung im alten Monat stehen:
						 * Im Feld stand 2027, der Kalender zeigte 2026 — und ein Klick
						 * auf einen Tag holte die alte Jahreszahl zurueck.
						 */
						openToDate={toWallClock(field.value) || undefined}
						isClearable={!props.disabled}
						onChange={date => field.onChange(fromWallClock(date))}
						showTimeSelect={props.showTimeSelect}
						timeFormat={TIME_FORMAT}
						timeCaption={t('Time')}
						locale={locale}
						placeholderText={props.placeholder}
						dateFormat={props.dateFormat}
						minDate={minDate}
						maxDate={maxDate}
					/>
				)}
			/>
		</label>
	)
}

DateTimeField.propTypes = {
	className: PropTypes.string,
	label: PropTypes.string.isRequired,
	error: PropTypes.object,
	control: PropTypes.object.isRequired,
	name: PropTypes.string.isRequired,
	placeholder: PropTypes.string,
	dateFormat: PropTypes.string,
	defaultValue: PropTypes.object,
	mb: PropTypes.number,
	disabled: PropTypes.bool,
	showTimeSelect: PropTypes.bool,
	minDate: PropTypes.object,
	maxDate: PropTypes.object,
	required: PropTypes.bool
}

DateTimeField.defaultProps = {
	dateFormat: 'dd.MM.yyyy',

	disabled: false
}
